/**
 * Assembles the full iframe HTML document from page state and renders it
 * into a blob URL. Handles global base CSS, theme, section CSS/HTML/JS,
 * editing overlays, broken image fallbacks, and the iframe bridge script.
 */

import {
  assembleThemeCSS,
  assembleSectionsCSS,
  assembleOverridesCSS,
  assembleButtonCSS,
  assembleFormCSS,
  INLINE_TEXT_COLOR_CSS,
  getEditingCSS
} from './css-assembler.js';
import { generateAllSectionScripts, generateSectionScriptTags } from './section-scripts.js';
import { renderGuestFrame } from '../preview/frame-host.js';
import { generateIframeBridgeScript } from '../editing/iframe-bridge.js';
import { store } from '../core/state.js';
import { getRemoteAssetFetcher } from '../core/asset-remote.js';
import { getButtonEffectJS, BUBBLY_BUTTON_JS } from './animation-injector.js';
import { DEFAULT_BUTTON_SELECTOR } from '../shared/button-vocabulary.js';
import { stampSectionRootAttribute } from '../shared/section-root-attr.js';
import {
  deriveNavActiveConvention,
  applyNavActiveConvention
} from '../shared/nav-active-convention.js';
import { reportError } from '../core/report-error.js';
import { formatSectionName, getCategoryMap } from '../shared/section-display-name.js';
import { bridge } from '../editing/bridge.js';
import {
  EDITABLE_SELECTORS,
  injectEditableAttrs as _injectEditableAttrs
} from '../editing/editable-utils.js';
import { getReorderableSpecs } from '../editing/pattern-adapters.js';
import { normalizeLogoSlots } from '../editing/logo-normalizer.js';
import { applyPreviewElementAttributeOverrides } from '../shared/element-attribute-overrides.js';
import { buildFontRuntimeCSS, buildFontRuntimeRule } from '../editing/font-runtime-css.js';
import {
  FONT_OPTIONS,
  buildLoaderTagsForFamily,
  collectElementFontWeights,
  collectInlineFontFamilies,
  slugifyFontName
} from '../shared/font-catalog.js';
import { getGlobalBaseCSS } from '../shared/global-base.js';
import { rewriteClientLinksInHTML } from '../shared/client-info.js';
import { applyEntranceAttrs, ENTRANCE_CANVAS_ATTR } from '../shared/entrance-presets.js';
import { assembleEntranceCSS } from '../shared/entrance-static-css.js';
import { resolveSectionEntrance, stateEmitsEntrance } from '../export/scroll-animation-shim.js';
import { applyAgencyCreditToHTML, resolveAgencyCredit } from '../shared/agency-credit.js';
import { textSlotProblem } from '../shared/slot-policy.js';
import {
  canonicalizeAssetBackedMediaHTML,
  canonicalizeAssetBackedMediaInState
} from '../shared/asset-html.js';
import { parseFirstFont } from '../shared/font-utils.js';
import {
  TEMPLATE_SUPPORT_ATTR,
  scopeTemplateSupportBlocks,
  templateSupportAttrValue,
  sectionNeedsTemplateSupportCSS,
  assembleLiveTemplateCSS,
  resolveComponentStripPlan,
  collectTemplateFontFamilies,
  rewriteSectionHtmlPreferVendored,
  warmVendoredAssetMaps,
  ensureVendoredAssetMap
} from '../shared/template-support-css.js';
import { collectVendoredFontFaceFamilies } from '../shared/template-font-rules.js';
import { resolveVendoredUrisSync } from '../shared/vendored-asset-resolve.js';
import { assembleTemplateFidelityCSS } from '../shared/template-fidelity-css.js';
import { assembleMotionStaticCSS } from '../shared/motion-static-css.js';
import {
  sourceTemplateElementAttrs,
  splitSourceTemplateSections,
  wrapSourceTemplateContent
} from '../shared/source-template-document.js';
import {
  assemblePreviewSiteEffectsCSS,
  assemblePreviewSiteEffectsJS,
  getGoTopRingHTML,
  loadSiteEffectPartials
} from '../site-effects/site-effects-loader.js';
import {
  ensureBehaviourRuntimes,
  BehaviourRuntimeMissingError
} from '../behaviours/behaviour-loader.js';

// Re-export for backward compatibility — consumers should migrate to editable-utils.js
export { EDITABLE_SELECTORS };

const PB_ASSET_RE = /pb-asset:\/\/([a-f0-9-]+)/g;

let currentBlobUrl = null;
let currentNonce = null;
const assetRenderUrlCache = new Map();
let pendingRevokeUrl = null;
let pendingRevokeWatchdogTimer = null;
let fontFallbackWarningShown = false;

// The button selector the last full render used. shell.js's surgical CSS push
// is synchronous and has no templateCSS in scope, but it does not need one: the
// vocabulary is a function of the SECTION SET, and any change to that forces a
// full re-render through this file. A button colour or hover-effect change --
// which is all the surgical path ever pushes -- cannot move it.
let _lastRenderedButtonSelector = DEFAULT_BUTTON_SELECTOR;
// What the TEMPLATE animates on hover by itself ('wipe' | 'bubble' | ''). The
// button panel reads it to say "Template default" instead of "None"; it never
// reaches emitted CSS.
let _lastRenderedTemplateHoverEffect = '';

export function getLastRenderedButtonSelector() {
  return _lastRenderedButtonSelector;
}

export function getLastRenderedTemplateHoverEffect() {
  return _lastRenderedTemplateHoverEffect;
}
loadSiteEffectPartials();

const RESTORE_FALLBACK_MS = 700;
const LOAD_FAILSAFE_MS = 6000;
const PENDING_REVOKE_WATCHDOG_MS = 30_000;
const IFRAME_SCROLL_ROOT_CSS = `
html,
body {
  height: 100%;
  margin: 0;
  overflow: hidden !important;
  scrollbar-gutter: auto;
  scrollbar-width: none;
  -ms-overflow-style: none;
}

html::-webkit-scrollbar,
body::-webkit-scrollbar {
  display: none;
  width: 0;
  height: 0;
}

#pb-scroll-root {
  position: relative;
  width: 100%;
  height: 100vh;
  min-height: 100%;
  overflow-x: hidden;
  overflow-y: auto;
  overscroll-behavior: contain;
  -webkit-overflow-scrolling: touch;
  scrollbar-gutter: auto;
  scrollbar-width: none;
  -ms-overflow-style: none;
}

#pb-scroll-root::-webkit-scrollbar {
  display: none;
  width: 0;
  height: 0;
}
`;
/**
 * The canvas dark-mode choice, as stored by the preview-mode toggle.
 *
 * Switching page rebuilds this document from scratch, so `body.dark-mode` —
 * written by the toggle through `pbToggleDarkMode` — was thrown away on every
 * navigation and the canvas snapped back to the light register. The toggle
 * already persists to localStorage `theme` (site-effects dark-mode.js, the same
 * key and values the exported site uses); nothing was reading it back.
 *
 * Deliberately narrower than the export's boot: only an EXPLICIT stored choice
 * counts. The export also falls back to `prefers-color-scheme`, which is right
 * for a visitor and wrong here — it would open the canvas in dark for every
 * OS-dark user who had never touched the toggle, and read as the builder
 * mis-rendering the template.
 *
 * Read here, in the PARENT, rather than by an inline boot script in the
 * document: the class is then present in the markup the parser sees, so there
 * is no frame in which the light register is painted, and <body>'s first child
 * stays the scroll root.
 *
 * @returns {'dark'|'light'|null} the stored choice, or null if there is none.
 */
export function savedCanvasThemeChoice() {
  try {
    const saved = globalThis.localStorage?.getItem('theme');
    return saved === 'dark' || saved === 'light' ? saved : null;
  } catch {
    // Storage is unavailable in sandboxed contexts and absent in Node (the
    // agent render worker) — an unreadable preference is simply no preference.
    return null;
  }
}

/**
 * Apply a theme choice to an already-serialized `<body>` attribute string.
 *
 * Both directions matter. A crawl that captured its source page in the dark
 * state ships `class="dark-mode"` on <body>, so an explicit LIGHT choice has to
 * be able to remove it — "add when dark" alone would make that template
 * impossible to view in its light register. `null` leaves the markup untouched.
 *
 * Merges into an existing `class="…"` rather than emitting a second one; the
 * template's own classes are preserved in order.
 */
export function withCanvasThemeClass(attrString, choice) {
  if (choice !== 'dark' && choice !== 'light') return attrString;
  const CLASS = 'dark-mode';
  if (!/\sclass="/.test(attrString)) {
    return choice === 'dark' ? `${attrString} class="${CLASS}"` : attrString;
  }
  return attrString.replace(/(\sclass=")([^"]*)(")/, (_, open, value, close) => {
    const classes = value.split(/\s+/).filter(Boolean);
    const has = classes.includes(CLASS);
    if (choice === 'dark' && !has) classes.push(CLASS);
    if (choice === 'light' && has) classes.splice(classes.indexOf(CLASS), 1);
    return `${open}${classes.join(' ')}${close}`;
  });
}

const IFRAME_SCROLL_ROOT_FACADE_JS = `
(function() {
  var root = document.getElementById('pb-scroll-root');
  if (!root || window.__PB_SCROLL_ROOT_FACADE__) return;
  window.__PB_SCROLL_ROOT_FACADE__ = true;
  window.__PB_SCROLL_ROOT__ = root;

  function toNumber(value, fallback) {
    var parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : fallback;
  }

  function getX() {
    return root.scrollLeft || 0;
  }

  function getY() {
    return root.scrollTop || 0;
  }

  function defineReadOnly(target, name, getter) {
    try {
      Object.defineProperty(target, name, { configurable: true, get: getter });
    } catch (err) {}
  }

  function defineScrollProp(target, name, getter, setter) {
    try {
      Object.defineProperty(target, name, { configurable: true, get: getter, set: setter });
    } catch (err) {}
  }

  defineReadOnly(window, 'scrollX', getX);
  defineReadOnly(window, 'pageXOffset', getX);
  defineReadOnly(window, 'scrollY', getY);
  defineReadOnly(window, 'pageYOffset', getY);
  defineReadOnly(document, 'scrollingElement', function() { return root; });

  defineScrollProp(document.documentElement, 'scrollTop', getY, function(value) {
    root.scrollTop = toNumber(value, root.scrollTop || 0);
  });
  defineScrollProp(document.body, 'scrollTop', getY, function(value) {
    root.scrollTop = toNumber(value, root.scrollTop || 0);
  });
  defineScrollProp(document.documentElement, 'scrollLeft', getX, function(value) {
    root.scrollLeft = toNumber(value, root.scrollLeft || 0);
  });
  defineScrollProp(document.body, 'scrollLeft', getX, function(value) {
    root.scrollLeft = toNumber(value, root.scrollLeft || 0);
  });

  function scrollRootTo(left, top, behavior) {
    if (typeof root.scrollTo === 'function') {
      if (behavior) {
        root.scrollTo({ left: left, top: top, behavior: behavior });
      } else {
        root.scrollTo(left, top);
      }
      return;
    }
    root.scrollLeft = left;
    root.scrollTop = top;
  }

  function scrollRootBy(left, top, behavior) {
    scrollRootTo(getX() + left, getY() + top, behavior);
  }

  window.scrollTo = function(x, y) {
    if (x && typeof x === 'object') {
      scrollRootTo(
        toNumber(x.left, root.scrollLeft || 0),
        toNumber(x.top, root.scrollTop || 0),
        x.behavior
      );
      return;
    }
    scrollRootTo(toNumber(x, root.scrollLeft || 0), toNumber(y, root.scrollTop || 0));
  };

  window.scrollBy = function(x, y) {
    if (x && typeof x === 'object') {
      scrollRootBy(toNumber(x.left, 0), toNumber(x.top, 0), x.behavior);
      return;
    }
    scrollRootBy(toNumber(x, 0), toNumber(y, 0));
  };

  function dispatchScroll(target) {
    try {
      target.dispatchEvent(new Event('scroll'));
    } catch (err) {}
  }

  root.addEventListener('scroll', function() {
    dispatchScroll(window);
    dispatchScroll(document);
  }, { passive: true });
})();
`;
const RENDER_TELEMETRY_PHASES = Object.freeze([
  'phase-assemble',
  'phase-resolve-assets',
  'phase-blob-create',
  'phase-iframe-load',
  'phase-bridge-ready',
  'phase-scroll-restore',
  'phase-reveal'
]);

let assetResolverWorker = null;
let assetResolverWorkerRequestId = 0;
const assetResolverWorkerPending = new Map();

function createAbortError(message = 'Render aborted') {
  const err = new Error(message);
  err.name = 'AbortError';
  return err;
}

function throwIfAborted(signal) {
  if (signal?.aborted) {
    throw createAbortError();
  }
}

function clearPendingRevokeWatchdog(clearTimeoutFn = clearTimeout) {
  if (pendingRevokeWatchdogTimer) {
    clearTimeoutFn(pendingRevokeWatchdogTimer);
    pendingRevokeWatchdogTimer = null;
  }
}

function revokePendingBlobUrl(clearTimeoutFn = clearTimeout) {
  if (!pendingRevokeUrl) return;
  const toRevoke = pendingRevokeUrl;
  pendingRevokeUrl = null;
  clearPendingRevokeWatchdog(clearTimeoutFn);
  try {
    URL.revokeObjectURL(toRevoke);
  } catch {
    // Ignore URL revocation errors.
  }
}

function warnFontFallbackOnce(pageState, fallbackFont) {
  const debugFonts =
    pageState?.flags?.debugFonts === true ||
    pageState?.debugFonts === true ||
    globalThis.__pbDebugFonts === true;
  if (!debugFonts || fontFallbackWarningShown || pageState?.font) return;
  fontFallbackWarningShown = true;
  console.warn(
    '[Renderer] No state.font set; falling back to',
    fallbackFont,
    '- template fonts may not have applied correctly'
  );
}

function setPendingBlobRevoke(
  url,
  renderNonce,
  { setTimeoutFn = setTimeout, clearTimeoutFn = clearTimeout } = {}
) {
  if (!url) return;
  if (pendingRevokeUrl && pendingRevokeUrl !== url) {
    revokePendingBlobUrl(clearTimeoutFn);
  }
  pendingRevokeUrl = url;
  clearPendingRevokeWatchdog(clearTimeoutFn);
  pendingRevokeWatchdogTimer = setTimeoutFn(() => {
    if (currentNonce !== renderNonce) return;
    console.warn('[Renderer] Blob revoke watchdog fired after 30000ms; revoking pending blob URL.');
    revokePendingBlobUrl(clearTimeoutFn);
  }, PENDING_REVOKE_WATCHDOG_MS);
}

function nowMs(__testHooks = null) {
  if (typeof __testHooks?.now === 'function') {
    return Number(__testHooks.now()) || 0;
  }
  if (globalThis.performance && typeof globalThis.performance.now === 'function') {
    return globalThis.performance.now();
  }
  return Date.now();
}

function canUsePerformanceMeasures(__testHooks = null) {
  if (__testHooks?.disablePerformanceMeasures) return false;
  const perf = globalThis.performance;
  return !!(perf && typeof perf.mark === 'function' && typeof perf.measure === 'function');
}

function createRenderTelemetry(renderId, __testHooks = null) {
  const startedAt = nowMs(__testHooks);
  const phases = {};
  const starts = {};
  const perfStarts = {};
  let perfMarkCounter = 0;
  const usePerformanceMeasures = canUsePerformanceMeasures(__testHooks);
  for (const phase of RENDER_TELEMETRY_PHASES) {
    phases[phase] = 0;
  }

  return {
    renderId,
    phases,
    start(phase) {
      if (!Object.hasOwn(phases, phase)) return;
      starts[phase] = nowMs(__testHooks);
      if (usePerformanceMeasures) {
        const perfStartName = `${phase}:start:${renderId}:${++perfMarkCounter}`;
        perfStarts[phase] = perfStartName;
        try {
          globalThis.performance.mark(perfStartName);
        } catch {
          // Ignore performance mark failures in older runtimes.
        }
      }
    },
    end(phase) {
      if (!Object.hasOwn(phases, phase)) return;
      const startValue = starts[phase];
      if (!Number.isFinite(startValue)) return;
      phases[phase] += Math.max(0, nowMs(__testHooks) - startValue);
      delete starts[phase];
      const perfStartName = perfStarts[phase];
      if (usePerformanceMeasures && perfStartName) {
        const perfEndName = `${phase}:end:${renderId}:${++perfMarkCounter}`;
        try {
          globalThis.performance.mark(perfEndName);
          globalThis.performance.measure(phase, perfStartName, perfEndName);
          if (typeof globalThis.performance.clearMarks === 'function') {
            globalThis.performance.clearMarks(perfStartName);
            globalThis.performance.clearMarks(perfEndName);
          }
        } catch {
          // Ignore measure failures — telemetry payload still carries phase timings.
        }
      }
      delete perfStarts[phase];
    },
    set(phase, durationMs) {
      if (!Object.hasOwn(phases, phase)) return;
      phases[phase] = Math.max(0, Number(durationMs) || 0);
      if (usePerformanceMeasures) {
        try {
          globalThis.performance.measure(phase, { duration: phases[phase] });
        } catch {
          // Ignore measure API shape mismatches.
        }
      }
    },
    snapshot(reason = '') {
      return {
        renderId,
        reason,
        totalMs: Math.max(0, nowMs(__testHooks) - startedAt),
        phases: { ...phases }
      };
    }
  };
}

function createAssetResolverWorker() {
  if (typeof Worker !== 'function') return null;
  try {
    const workerCode = `
      self.onmessage = function(event) {
        const data = event && event.data ? event.data : {};
        const requestId = data.requestId;
        const blob = data.blob;
        try {
          if (!blob || typeof FileReaderSync !== 'function') {
            throw new Error('FileReaderSync unavailable');
          }
          const reader = new FileReaderSync();
          const dataUrl = reader.readAsDataURL(blob);
          self.postMessage({ requestId: requestId, ok: true, dataUrl: dataUrl });
        } catch (err) {
          self.postMessage({
            requestId: requestId,
            ok: false,
            error: err && err.message ? String(err.message) : 'off-thread conversion failed'
          });
        }
      };
    `;
    const scriptBlob = new Blob([workerCode], { type: 'text/javascript' });
    const scriptUrl = URL.createObjectURL(scriptBlob);
    const worker = new Worker(scriptUrl);
    URL.revokeObjectURL(scriptUrl);
    worker.addEventListener('message', (event) => {
      const payload = event?.data || {};
      const pending = assetResolverWorkerPending.get(payload.requestId);
      if (!pending) return;
      assetResolverWorkerPending.delete(payload.requestId);
      if (payload.ok) {
        pending.resolve(typeof payload.dataUrl === 'string' ? payload.dataUrl : '');
        return;
      }
      pending.reject(new Error(payload.error || 'off-thread conversion failed'));
    });
    worker.addEventListener('error', (event) => {
      const pendingRequests = Array.from(assetResolverWorkerPending.values());
      assetResolverWorkerPending.clear();
      for (const pending of pendingRequests) {
        pending.reject(event?.error || new Error(event?.message || 'asset worker error'));
      }
      try {
        worker.terminate();
      } catch {
        // Ignore worker termination failures.
      }
      assetResolverWorker = null;
    });
    return worker;
  } catch (err) {
    console.warn('[Renderer] Off-thread asset worker unavailable:', err?.message || err);
    return null;
  }
}

function ensureAssetResolverWorker() {
  if (assetResolverWorker) return assetResolverWorker;
  assetResolverWorker = createAssetResolverWorker();
  return assetResolverWorker;
}

async function blobToDataUrlOffThread(blob, { signal = null } = {}) {
  const worker = ensureAssetResolverWorker();
  if (!worker) return blobToDataUrl(blob, { signal });
  throwIfAborted(signal);
  const requestId = ++assetResolverWorkerRequestId;
  return await new Promise((resolve, reject) => {
    const onAbort = signal
      ? () => {
          assetResolverWorkerPending.delete(requestId);
          reject(createAbortError());
        }
      : null;

    if (signal) signal.addEventListener('abort', onAbort, { once: true });
    assetResolverWorkerPending.set(requestId, {
      resolve: (value) => {
        if (signal && onAbort) signal.removeEventListener('abort', onAbort);
        resolve(value);
      },
      reject: (err) => {
        if (signal && onAbort) signal.removeEventListener('abort', onAbort);
        reject(err);
      }
    });

    try {
      worker.postMessage({ requestId, blob });
    } catch (err) {
      assetResolverWorkerPending.delete(requestId);
      if (signal && onAbort) signal.removeEventListener('abort', onAbort);
      reject(err);
    }
  });
}

// Base layer inlined at build time (scripts/shared/global-base.js). The preview
// used to fetch data/global-base.css and silently fall back to an empty base
// layer on failure — the same silent-loss bug as the exporters. That path is gone.
export async function loadGlobalBaseCSS() {
  return getGlobalBaseCSS();
}

export function injectEditableAttributes(html, { clientEditMode = false } = {}) {
  const parser = new DOMParser();
  const doc = parser.parseFromString(`<div>${html}</div>`, 'text/html');
  const root = doc.body.firstElementChild;
  normalizeLogoSlots(root);
  _injectEditableAttrs(root);
  if (clientEditMode) {
    for (const editable of root.querySelectorAll('[data-pb-editable="text"]')) {
      if (textSlotProblem(editable) !== null) {
        editable.setAttribute('data-pb-client-locked', '');
      }
    }
  }
  return root.innerHTML;
}

function escapeHTMLAttr(value) {
  return String(value == null ? '' : value)
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

function templateSiteAttr(section, sourceTemplateDomain = null) {
  const siteName = templateSupportAttrValue(section?.templateSiteName);
  const sourceName = templateSupportAttrValue(sourceTemplateDomain);
  return siteName &&
    siteName !== sourceName &&
    sectionNeedsTemplateSupportCSS(section, sourceTemplateDomain)
    ? ` ${TEMPLATE_SUPPORT_ATTR}="${escapeHTMLAttr(siteName)}"`
    : '';
}

const TEMPLATE_OVERLAY_RE =
  /\b(?:modal|popup|pop-up|dialog|cookie(?:\s|-)?banner|cookie(?:\s|-)?notice)\b/i;

function isTemplateOverlaySection(section) {
  if (!section) return false;
  const meta = [
    section.category,
    section.description,
    section.sectionId,
    section.catalogKey,
    section.templateSectionId
  ]
    .filter(Boolean)
    .join(' ');
  if (TEMPLATE_OVERLAY_RE.test(meta)) return true;

  const html = String(section.html || '');
  const css = String(section.css || '');
  return /\b(?:modal|popup|dialog)\b/i.test(html) && /position\s*:\s*fixed/i.test(css);
}

function templateOverlayAttr(section) {
  return isTemplateOverlaySection(section) ? ' data-pb-template-overlay="true"' : '';
}

function injectSectionAttribute(html, sectionId) {
  return stampSectionRootAttribute(html, sectionId);
}

function buildSectionHTML(
  section,
  index,
  clientInfo,
  projectName,
  sourceTemplateDomain = null,
  entrance = null,
  clientEditMode = false
) {
  // A VENDORED BYTE WINS; a hotlink is the fallback. Same rule the section CSS
  // has followed since 2026-08-26, applied to the markup it was never applied
  // to. FIRST in the chain, on the raw source HTML, because every later pass
  // stamps builder attributes and an image URL is easier to match before the
  // markup grows. Warmed by warmVendoredAssetMaps before assembleDocument runs.
  let html = rewriteSectionHtmlPreferVendored(section.html, section.templateSiteName);
  html = rewriteClientLinksInHTML(html, clientInfo, 'preview', projectName);
  html = applyPreviewElementAttributeOverrides(html, section);
  html = injectEditableAttributes(html, { clientEditMode });
  html = injectSectionAttribute(html, section.sectionId);
  // EA-3. Stamped on the canvas as well as in exports, so what the user
  // configures is what Preview animates without a second render path. The
  // canvas document carries `data-pb-entrance-canvas`, so the runtime holds
  // every one of these force-revealed until an explicit Preview enter.
  html = stampEntranceInHTML(html, entrance);

  // Controls live in the parent glass overlay — not inside the iframe
  const controlsHTML = '';
  const label = escapeHTMLAttr(formatSectionName(section));

  return `<div class="pb-section-wrapper" data-pb-instance="${section.instanceId}" data-pb-category="${section.category || ''}" data-pb-label="${label}" data-pb-type="${section.type || 'template'}"${templateOverlayAttr(section)}${templateSiteAttr(section, sourceTemplateDomain)} style="--pb-section-index:${index}">
  ${controlsHTML}
  ${html}
</div>`;
}

/**
 * Apply the builder entrance to one section's canvas markup.
 *
 * A string wrapper because the canvas render pipeline is string-to-string; the
 * ENGINE is the shared one (`applyEntranceAttrs`), so the canvas and the four
 * generated-document surfaces cannot disagree about what a preset stamps.
 */
function stampEntranceInHTML(html, entrance) {
  if (!entrance || typeof DOMParser === 'undefined') return html;
  try {
    const doc = new DOMParser().parseFromString(`<div>${html}</div>`, 'text/html');
    const wrapper = doc.body.firstElementChild;
    const root = wrapper?.firstElementChild;
    if (!root) return html;
    applyEntranceAttrs(root, entrance);
    return wrapper.innerHTML;
  } catch (err) {
    // Expected-flow fallback: a section that will not parse still renders, just
    // without its entrance. Never worth failing a canvas render over.
    reportError('IframeRenderer.stampEntrance', err, { user: 'silent' });
    return html;
  }
}

/**
 * Highlight the current page's nav link on the canvas.
 *
 * Before this, the canvas marked nothing: what you saw was whatever the crawler
 * froze, so herocaredemo highlighted "Home" on every page. The convention is
 * derived from the nav section itself — see shared/nav-active-convention.js.
 */
function markNavActiveLink(html, section, currentPage) {
  if (typeof DOMParser === 'undefined') return html;
  try {
    const doc = new DOMParser().parseFromString(`<div>${html}</div>`, 'text/html');
    const root = doc.body.firstElementChild;
    if (!root) return html;
    const convention = deriveNavActiveConvention(section);
    applyNavActiveConvention(root, currentPage, convention);
    return root.innerHTML;
  } catch (err) {
    // Expected-flow fallback: a nav that will not parse still renders, just
    // without the active highlight. Never worth failing a render over.
    reportError('IframeRenderer.markNavActiveLink', err, { user: 'silent' });
    return html;
  }
}

/**
 * Show the exporting agency's credit on the canvas footer.
 *
 * Preview-only, like markNavActiveLink: nothing is written back to state, so
 * the template's real studio survives in the section data and in the "Built by"
 * filter. What this buys is that the footer you edit is the footer you ship.
 */
function markAgencyCredit(html) {
  if (typeof DOMParser === 'undefined') return html;
  try {
    return applyAgencyCreditToHTML(html, resolveAgencyCredit(store.getState?.() || null));
  } catch (err) {
    // Expected-flow fallback: a footer that will not parse still renders, just
    // with the template's original credit. Never worth failing a render over.
    reportError('IframeRenderer.markAgencyCredit', err, { user: 'silent' });
    return html;
  }
}

function buildSharedSectionHTML(
  section,
  type,
  clientInfo,
  projectName,
  sourceTemplateDomain = null,
  currentPage = null,
  clientEditMode = false
) {
  if (!section) return '';
  let html = rewriteSectionHtmlPreferVendored(section.html, section.templateSiteName);
  html = rewriteClientLinksInHTML(html, clientInfo, 'preview', projectName);
  html = applyPreviewElementAttributeOverrides(html, section);
  html = injectEditableAttributes(html, { clientEditMode });
  // AFTER injectEditableAttributes: that pass stamps data-pb-eid, and marking a
  // link before it would churn the identity the selection machinery keys on.
  // BEFORE injectSectionAttribute, which strips the section ROOT id only —
  // descendant ids like #active-menu survive, so an id-kind marker works here.
  if (type === 'navigation' && currentPage) html = markNavActiveLink(html, section, currentPage);
  // Same seam, same reason as the nav marker above: AFTER injectEditableAttributes
  // so the eids the selection machinery keys on are already assigned and this
  // pass cannot churn them. The canvas shows the credit the export will ship;
  // section.html on disk still carries the source template's studio.
  if (type === 'footer') html = markAgencyCredit(html);
  html = injectSectionAttribute(html, section.sectionId);
  const fallback = type === 'navigation' ? 'Navigation' : type === 'footer' ? 'Footer' : 'Section';
  const label = escapeHTMLAttr(formatSectionName(section, { fallback }));
  return `<div data-pb-instance="${section.instanceId}" data-pb-shared="${type}" data-pb-label="${label}"${templateSiteAttr(section, sourceTemplateDomain)}>
  ${html}
</div>`;
}

export async function buildPreviewSectionInnerHTML(section, pageState = {}, options = {}) {
  if (!section) return '';
  const { clientInfo, projectName } = pageState || {};
  // The surgical path has no state-wide resolution pass ahead of it — this is
  // what `pushPreviewSectionHTML` uses for a live edit — so it must warm its own
  // map. Without this an edited section silently reverts to hotlinking while the
  // full render beside it resolves, which is exactly the kind of split that let
  // the CSS half of this rule look finished for months.
  await ensureVendoredAssetMap(section.templateSiteName);
  let html = rewriteSectionHtmlPreferVendored(section.html || '', section.templateSiteName);
  html = rewriteClientLinksInHTML(html, clientInfo, 'preview', projectName);
  html = await resolvePbAssetUrisInString(html, {
    signal: options.signal || null,
    offThreadAssets: !!options.offThreadAssets
  });
  html = applyPreviewElementAttributeOverrides(html, section);
  html = injectEditableAttributes(html, {
    clientEditMode: pageState?.flags?.clientEditMode === true
  });
  return injectSectionAttribute(html, section.sectionId);
}

/**
 * Build per-family <link> tags using the source-aware font catalog. Fontshare
 * families hit Fontshare, Google families hit Google Fonts, and system fonts
 * emit nothing.
 *
 * The optional `data-pb-inline-font="<slug>"` marker is also set by the bridge
 * live-inject path for inline span fonts so repeated emissions don't duplicate.
 */
function buildFontLoaderLinks(
  families,
  { excluded = new Set(), inlineMarker = false, weights, role = '' } = {}
) {
  const out = [];
  const seen = new Set();
  for (const family of families || []) {
    if (!family || family === 'Roboto' || excluded.has(family) || seen.has(family)) continue;
    seen.add(family);
    const loader = buildLoaderTagsForFamily(family, weights ? { weights } : {});
    if (!loader) continue;
    const slug = slugifyFontName(family);
    const marker = inlineMarker ? ` data-pb-inline-font="${escapeHTMLAttr(slug)}"` : '';
    const roleAttrs =
      role === 'body' || role === 'heading'
        ? ` id="pb-${role}-font-loader" data-pb-font-role="${role}" data-pb-font-family="${escapeHTMLAttr(slug)}"`
        : '';
    out.push(`<link rel="stylesheet" href="${escapeHTMLAttr(loader.href)}"${marker}${roleAttrs}>`);
  }
  return out.join('\n  ');
}

/**
 * Every `templateSiteName` a render of this state will touch — the active page's
 * sections plus the shared nav/footer, matching exactly what `assembleDocument`
 * builds. Scoped to the rendered set for the same reason `resolveAssetUris` is:
 * walking every hydrated page would grow with the project and undo the lazy-load.
 */
export function collectRenderedTemplateSiteNames(state) {
  const names = new Set();
  const add = (section) => {
    const name =
      section && typeof section.templateSiteName === 'string'
        ? section.templateSiteName.trim()
        : '';
    if (name) names.add(name);
  };
  if (!state) return names;
  add(state.sharedSections?.navigation);
  add(state.sharedSections?.footer);
  const pages = Array.isArray(state.pages) ? state.pages : [];
  const activePage = pages.find((p) => p.id === state.activePage) || pages[0];
  for (const section of activePage?.sections || []) add(section);
  return names;
}

/**
 * `assembleDocument` with its vendored maps guaranteed warm.
 *
 * This exists so the warm and the assembly cannot be separated. They were two
 * statements in `renderToIframe` for one commit, and a test that warmed on its
 * own behalf could not tell whether the renderer warmed at all — deleting the
 * warm left every assertion green. Anything that renders a full document calls
 * THIS, never `assembleDocument` directly.
 */
export async function assembleDocumentWarmed(state, ...rest) {
  // BC-14: the nameplate (`data-pb-label`) is stamped here, synchronously, so
  // the category map must be in hand first or the canvas names a section
  // differently from the list beside it. Fails soft to the title-cased rung.
  await Promise.all([
    warmVendoredAssetMaps(collectRenderedTemplateSiteNames(state)),
    getCategoryMap()
  ]);
  // AG-4 step 2. The canvas is a VIEW, so a runtime that will not load degrades
  // the widget to its captured static state rather than failing the render —
  // but it is never silent, because the section would then look correct and
  // simply stop responding, and the next thing the user does is export it.
  //
  // The loader caches successes only, so this is a real retry on every render:
  // a widget disabled by one bad response comes back on the next one.
  const behaviourLoad = await ensureBehaviourRuntimes(state);
  if (!behaviourLoad.ok) {
    reportError(
      'renderer.behaviours',
      new BehaviourRuntimeMissingError(
        behaviourLoad.missing,
        [...behaviourLoad.errors.values()].join('; ')
      ),
      {
        user: 'toast',
        message: `Interactive content is showing as static (${behaviourLoad.missing.join(', ')}). Reload to retry.`
      }
    );
  }
  return assembleDocument(state, ...rest);
}

export function assembleDocument(
  pageState,
  nonce,
  globalCSS,
  siteEffectsCSS = '',
  siteEffectsJS = '',
  goTopRingHTML = '',
  templateCSS = '',
  { guestFrame = false } = {}
) {
  const {
    theme,
    font,
    headingFont,
    headingFontWeight,
    iconTinting,
    sharedSections,
    pages,
    activePage,
    buttonStyles,
    formStyles,
    clientInfo,
    projectName,
    sourceTemplateDomain
  } = pageState;
  const page = pages.find((p) => p.id === activePage) || pages[0];
  if (!page) return '<html><body><p>No page found</p></body></html>';
  const sourceTemplateDocument = page.sourceTemplateDocument || pageState.sourceTemplateDocument;

  const allSections = [];
  if (sharedSections.navigation) allSections.push(sharedSections.navigation);
  allSections.push(...page.sections);
  if (sharedSections.footer) allSections.push(sharedSections.footer);

  const themeCSS = assembleThemeCSS(theme);
  // One plan per render: it carries both the component-strip flags and this
  // project's derived button vocabulary, so the button CSS and the button
  // effect JS below cannot disagree about what counts as a button.
  const componentStripPlan = resolveComponentStripPlan(pageState, templateCSS);
  _lastRenderedButtonSelector = componentStripPlan.buttonSelector;
  _lastRenderedTemplateHoverEffect = componentStripPlan.templateHoverEffect || '';
  // `pb-vendored://` reaches SECTION css now, not only the support sheet: a
  // template's background images resolve from its reference bundle when it has
  // them. Resolved here rather than inside the assembler because the assembler
  // is shared with surfaces that resolve the scheme their own way (the saved
  // preview inlines data: URIs, the exporters bundle the file).
  const sectionsCSS = resolveVendoredUrisSync(
    // `fontSource` rides along so the assembler can tell a theme font someone
    // CHOSE from one a template load merely derived — see css-assembler.js.
    assembleSectionsCSS(allSections, { ...componentStripPlan, fontSource: pageState?.fontSource })
  );
  const templateFidelityCSS = assembleTemplateFidelityCSS(allSections);
  const motionStaticCSS = assembleMotionStaticCSS(allSections);
  // Include shared nav/footer so paste / Format-Painter overrides on a
  // shared-section element also render in the preview. Without this, an
  // override written on sharedSections.navigation.overrides.elements[eid]
  // would silently fail to apply until a full re-render path went through
  // page sections only.
  const overridesCSS = assembleOverridesCSS(allSections);
  const buttonCSS = assembleButtonCSS(buttonStyles, componentStripPlan.buttonSelector);
  const formCSS = assembleFormCSS(formStyles);
  const entranceCSS = stateEmitsEntrance(pageState) ? assembleEntranceCSS() : '';
  // pb-vendored:// has no meaning to a browser; the preview resolves it to the
  // manifest/R2 URL for the binary, which is why a template's own typeface
  // survives the source site going away.
  const preparedTemplateCSS = resolveVendoredUrisSync(
    scopeTemplateSupportBlocks(templateCSS || '')
  );
  const sectionScriptTags = guestFrame
    ? generateSectionScriptTags(allSections)
    : `<script>${generateAllSectionScripts(allSections)}</script>`;
  const bridgeScript = generateIframeBridgeScript(nonce, {
    guestFrame,
    iconTinting: iconTinting !== false,
    reorderableSpecs: getReorderableSpecs(),
    allowedFontSlugs: FONT_OPTIONS.map(slugifyFontName),
    inlineFontRules: Object.fromEntries(
      FONT_OPTIONS.map((family) => [slugifyFontName(family), buildFontRuntimeRule(family)])
    ),
    inlineFontLoaders: Object.fromEntries(
      FONT_OPTIONS.map((family) => [slugifyFontName(family), buildLoaderTagsForFamily(family)])
    )
  });

  const navHTML = buildSharedSectionHTML(
    sharedSections.navigation,
    'navigation',
    clientInfo,
    projectName,
    sourceTemplateDomain,
    page,
    pageState?.flags?.clientEditMode === true
  );
  const footerHTML = buildSharedSectionHTML(
    sharedSections.footer,
    'footer',
    clientInfo,
    projectName,
    sourceTemplateDomain,
    null,
    pageState?.flags?.clientEditMode === true
  );
  const sectionCount = page.sections.length;
  const placedSections = splitSourceTemplateSections(
    page.sections.map((section, index) => ({ section, index })),
    page.sourceTemplateSectionPlacement,
    ({ section }) => section?.templateSectionId || section?.sectionId
  );
  const renderPlacedSections = (entries) =>
    entries
      .map(({ section, index }) =>
        buildSectionHTML(
          section,
          index,
          clientInfo,
          projectName,
          sourceTemplateDomain,
          // PAGE sections only — the nav and footer builds below do not stamp.
          resolveSectionEntrance(pageState, section),
          pageState?.flags?.clientEditMode === true
        )
      )
      .join('\n');
  const beforeWrapperHTML = renderPlacedSections(placedSections.beforeWrapper);
  const mainHTML = renderPlacedSections(placedSections.insideWrapper);
  const afterWrapperHTML = renderPlacedSections(placedSections.afterWrapper);

  const bodyFont = parseFirstFont(font) || 'Roboto';
  const elementFontWeights = collectElementFontWeights(pageState);
  const hWeight = headingFontWeight || '700';
  const previewWeights = Array.from(
    new Set(['400', '700', '900', String(hWeight), ...elementFontWeights])
  );
  warnFontFallbackOnce(pageState, bodyFont);
  // IF-4: a family whose real binaries ship as vendored @font-face rules must
  // NOT also be hotlinked from Google Fonts. Two face sets for one family name
  // is a race the template loses — Google serves static instances, the vendored
  // file is usually the variable font the site actually designed with.
  const vendoredFontFamilies = new Set(
    collectVendoredFontFaceFamilies(templateCSS).map((family) => family.toLowerCase())
  );
  const isVendoredFamily = (family) =>
    !!family && vendoredFontFamilies.has(String(family).toLowerCase());
  const fontLink =
    bodyFont && bodyFont !== 'Roboto' && !isVendoredFamily(bodyFont)
      ? buildFontLoaderLinks([bodyFont], { weights: previewWeights, role: 'body' })
      : '';
  const fontStyle = `body { font-family: '${bodyFont}', 'Roboto', Arial, sans-serif; }`;

  const effectiveHeadingFont = parseFirstFont(headingFont) || bodyFont;
  const headingFontLink =
    effectiveHeadingFont &&
    effectiveHeadingFont !== bodyFont &&
    effectiveHeadingFont !== 'Roboto' &&
    !isVendoredFamily(effectiveHeadingFont)
      ? buildFontLoaderLinks([effectiveHeadingFont], { weights: previewWeights, role: 'heading' })
      : '';
  // A template-DERIVED heading font is a readout, not a choice (`fontSource`
  // in core/state.js), and this block is not only a family: it forces a
  // WEIGHT. fourhorsemenmedia.com sets every heading to 300 and its Champion
  // Gothic Lightweight face; the builder read the family off the template,
  // re-declared it here with `font-weight: 700`, and bolded every heading on
  // the site — after the template's own cascade, at equal specificity. While
  // the fonts are the template's own there is nothing to apply; the block
  // returns the moment someone actually picks a heading font.
  const headingFontStyle =
    effectiveHeadingFont &&
    effectiveHeadingFont !== bodyFont &&
    pageState?.fontSource !== 'template'
      ? `h1,h2,h3,h4,h5,h6,.cs-title,.cs-h3,.cs-topper { font-family: '${effectiveHeadingFont}', 'Roboto', Arial, sans-serif; font-weight: ${hWeight}; }`
      : '';
  const templateFontLink = buildFontLoaderLinks(
    collectTemplateFontFamilies(templateCSS).filter(
      (family) =>
        family !== bodyFont && family !== effectiveHeadingFont && !isVendoredFamily(family)
    )
  );

  // <link> tags for inline-font usage (pb-font-<slug> spans created by the
  // text-format panel). Source-aware so Fontshare families resolve via
  // Fontshare and system fonts emit nothing. Body / heading / template
  // families are excluded — they already have their own link above.
  const alreadyLoaded = new Set([bodyFont, effectiveHeadingFont]);
  for (const family of collectTemplateFontFamilies(templateCSS)) alreadyLoaded.add(family);
  for (const family of collectVendoredFontFaceFamilies(templateCSS)) alreadyLoaded.add(family);
  const inlineFontLink = buildFontLoaderLinks(collectInlineFontFamilies(pageState), {
    excluded: alreadyLoaded,
    inlineMarker: true
  });
  const inlineFontRuntimeCSS = buildFontRuntimeCSS(collectInlineFontFamilies(pageState));

  // Deterministic theme vars consumed by normalizeSectionTypography (section CSS
  // wraps hardcoded font-family declarations with `var(--pb-heading-font, <orig>)`
  // / `var(--pb-body-font, <orig>)`). Emitted regardless of whether heading
  // differs from body, so the normalizer's fallback never accidentally activates.
  const pbThemeFontVars = `:root { --pb-body-font: '${bodyFont}', 'Roboto', Arial, sans-serif; --pb-heading-font: '${effectiveHeadingFont}', 'Roboto', Arial, sans-serif; }`;
  const renderedTemplateContent = `
    ${navHTML}
    <main style="--pb-section-count:${sectionCount}">
      ${mainHTML}
    </main>
    ${footerHTML}
    ${goTopRingHTML || ''}`;
  const wrappedTemplateContent = wrapSourceTemplateContent(
    sourceTemplateDocument,
    renderedTemplateContent
  );
  const templateContent =
    beforeWrapperHTML || afterWrapperHTML
      ? `${beforeWrapperHTML ? `${beforeWrapperHTML}\n` : ''}${wrappedTemplateContent}${
          afterWrapperHTML ? `\n${afterWrapperHTML}` : ''
        }`
      : wrappedTemplateContent;

  // D4 — suppression must be SYNCHRONOUS WITH THE DOCUMENT, not a message.
  //
  // The scroll runtime is appended LAST into the bridge script and auto-inits
  // at parse time, before the parent can reliably send any mode message. A
  // stamped below-fold node would therefore arm and go invisible for however
  // long that round trip takes. The marker makes suppression a property of the
  // document the renderer built, so there is no window at all.
  //
  // The canvas deliberately carries NO `data-pb-entrance-template`: per the
  // locked decision, template conventions keep today's canvas behaviour in
  // every editing surface, and turning them off takes effect in the real
  // Preview and in generated documents.
  return `<!DOCTYPE html>
<html lang="en" ${ENTRANCE_CANVAS_ATTR}${sourceTemplateElementAttrs(sourceTemplateDocument, 'html')}>
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  ${fontLink}
  ${headingFontLink}
  ${templateFontLink}
  ${inlineFontLink}
  <style id="pb-global-base">${globalCSS || ''}</style>
  <style id="pb-template-css">${preparedTemplateCSS}</style>
  <style id="pb-theme">${themeCSS}</style>
  <style id="pb-font-vars">${pbThemeFontVars}</style>
  <style id="pb-font">${fontStyle}</style>
  <style id="pb-heading-font">${headingFontStyle}</style>
  <style id="pb-sections-css">${sectionsCSS}</style>
  <style id="pb-template-fidelity">${templateFidelityCSS}</style>
  <style id="pb-motion-static">${motionStaticCSS}</style>
  <style id="pb-overrides">${overridesCSS}</style>
  <style id="pb-button-overrides">${buttonCSS}</style>
  <style id="pb-form-overrides">${formCSS}</style>
  <style id="pb-inline-text-colors">${INLINE_TEXT_COLOR_CSS}</style>
  <style id="pb-inline-font-rules">${inlineFontRuntimeCSS}</style>
  ${entranceCSS ? `<style id="pb-entrance-css">${entranceCSS}</style>` : ''}
  <style id="pb-site-effects">${siteEffectsCSS || ''}</style>
  <style id="pb-scroll-root-css">${IFRAME_SCROLL_ROOT_CSS}</style>
  <style id="pb-editing">${getEditingCSS()}</style>
</head>
<body${withCanvasThemeClass(sourceTemplateElementAttrs(sourceTemplateDocument, 'body'), savedCanvasThemeChoice())}${pageState?.flags?.clientEditMode ? ' data-pb-client-mode=""' : ''}>
  <div id="pb-scroll-root">
    ${templateContent}
  </div>

  <script>${IFRAME_SCROLL_ROOT_FACADE_JS}</script>
  <script>${bridgeScript}</script>
  ${sectionScriptTags}
  <script id="pb-button-effect-js">${getButtonEffectJS({ buttonStyles }, componentStripPlan.buttonSelector)}</script>
  <script id="pb-bubbly-button-js">${BUBBLY_BUTTON_JS}</script>
  <script id="pb-site-effects-js">${siteEffectsJS || ''}</script>
</body>
</html>`;
}

function blobToDataUrl(blob, { signal = null } = {}) {
  return new Promise((resolve, reject) => {
    throwIfAborted(signal);
    const reader = new FileReader();
    const cleanup = () => {
      if (signal && onAbort) signal.removeEventListener('abort', onAbort);
    };
    const onLoad = () => {
      cleanup();
      resolve(typeof reader.result === 'string' ? reader.result : '');
    };
    const onError = () => {
      cleanup();
      reject(reader.error || new Error('Failed to convert blob to data URL'));
    };
    const onAbort = signal
      ? () => {
          try {
            reader.abort();
          } catch {
            // Ignore abort errors.
          }
          cleanup();
          reject(createAbortError());
        }
      : null;
    reader.onload = onLoad;
    reader.onerror = onError;
    if (signal) signal.addEventListener('abort', onAbort, { once: true });
    reader.readAsDataURL(blob);
  });
}

function collectPbAssetIdsFromString(value, assetIds) {
  if (typeof value !== 'string' || !assetIds) return;
  PB_ASSET_RE.lastIndex = 0;
  for (const match of value.matchAll(PB_ASSET_RE)) {
    assetIds.add(match[1]);
  }
}

async function resolveAssetIdsToDataUrls(
  assetIds,
  { onProgress = null, signal = null, offThreadAssets = false, assetSource = null } = {}
) {
  throwIfAborted(signal);
  const ids = Array.from(assetIds || []);
  const totalAssets = ids.length;
  const urlMap = new Map();
  const reportProgress = (current, total) => {
    if (typeof onProgress !== 'function') return;
    try {
      onProgress({ current, total });
    } catch (err) {
      console.warn('[Renderer] Asset progress callback failed:', err);
    }
  };

  if (totalAssets === 0) {
    reportProgress(0, 0);
    return urlMap;
  }

  let resolvedAssets = 0;
  reportProgress(0, totalAssets);
  await Promise.all(
    ids.map(async (id) => {
      throwIfAborted(signal);
      try {
        // An old version bundle is authoritative for its own asset ids. It must
        // run before both the id-only live cache and IndexedDB: the live project
        // may have deleted the blob, or may reuse the id for different bytes.
        let record = assetSource ? await assetSource.get(id) : null;
        throwIfAborted(signal);
        const resolvedFromInjectedSource = !!record?.blob;

        if (!resolvedFromInjectedSource && assetRenderUrlCache.has(id)) {
          urlMap.set(id, assetRenderUrlCache.get(id));
          return;
        }

        if (!resolvedFromInjectedSource) {
          record = await store.assets.get(id);
          throwIfAborted(signal);
        }
        if (!record || !record.blob) {
          // Collab Phase 4: a collaborator's blob may exist only on the server.
          // The installed fetcher (collab-assets.js) handles in-flight dedupe,
          // the 30 s negative cache, and persisting the record locally.
          const fetchRemote = getRemoteAssetFetcher();
          if (fetchRemote) {
            record = await fetchRemote(id).catch(() => null);
            throwIfAborted(signal);
          }
        }
        if (!record || !record.blob) return;

        // JSZip's async('blob') does not preserve the entry MIME type. The
        // bundle source supplies it beside the Blob; re-type those bytes before
        // FileReader builds the data URL or Chromium rejects the image and the
        // iframe's broken-image fallback hides the comparison defect.
        const renderBlob =
          record.blob.type || !record.mime
            ? record.blob
            : new Blob([record.blob], { type: record.mime });
        const dataUrl = offThreadAssets
          ? await blobToDataUrlOffThread(renderBlob, { signal })
          : await blobToDataUrl(renderBlob, { signal });
        if (!dataUrl) return;

        // Bundle-scoped bytes cannot enter the global id-only cache. Doing so
        // would make a later live render inherit whichever historical version
        // happened to be reviewed most recently.
        if (!resolvedFromInjectedSource) assetRenderUrlCache.set(id, dataUrl);
        urlMap.set(id, dataUrl);
      } catch (error) {
        throwIfAborted(signal);
        // A supplied bundle is the comparison's authority. If reading or
        // converting its bytes fails, fail the review render instead of
        // silently painting a broken image and presenting it as ground truth.
        if (assetSource) throw error;
        // Asset not found — leave URI as-is (broken image handler will catch it)
      } finally {
        resolvedAssets += 1;
        reportProgress(resolvedAssets, totalAssets);
      }
    })
  );

  return urlMap;
}

function replacePbAssetUris(value, urlMap) {
  if (typeof value !== 'string' || !urlMap || urlMap.size === 0) return value;
  PB_ASSET_RE.lastIndex = 0;
  return value.replace(PB_ASSET_RE, (match, id) => urlMap.get(id) || match);
}

async function resolvePbAssetUrisInString(value, options = {}) {
  if (typeof value !== 'string') return value;
  const canonical = canonicalizeAssetBackedMediaHTML(value);
  if (!canonical.includes('pb-asset://')) return canonical;
  const assetIds = new Set();
  collectPbAssetIdsFromString(canonical, assetIds);
  const urlMap = await resolveAssetIdsToDataUrls(assetIds, options);
  return replacePbAssetUris(canonical, urlMap);
}

/**
 * Deep-clone state and resolve all pb-asset://{id} URIs in section HTML/CSS
 * to render-safe URLs for iframe display. State itself is NOT mutated.
 */
async function resolveAssetUris(
  pageState,
  { onProgress = null, signal = null, offThreadAssets = false, assetSource = null } = {}
) {
  throwIfAborted(signal);
  const resolved = structuredClone(pageState);
  canonicalizeAssetBackedMediaInState(resolved);

  // Collect all unique asset IDs. Preview only renders the active page +
  // shared nav/footer, so we only scan those — without this, every render
  // after background hydration would re-walk growing numbers of pages and
  // negate the lazy-load win.
  const assetIds = new Set();
  const allSections = [];
  if (resolved.sharedSections.navigation) allSections.push(resolved.sharedSections.navigation);
  if (resolved.sharedSections.footer) allSections.push(resolved.sharedSections.footer);
  const activePage = resolved.pages.find((p) => p.id === resolved.activePage) || resolved.pages[0];
  if (activePage && Array.isArray(activePage.sections)) {
    allSections.push(...activePage.sections);
  }

  for (const section of allSections) {
    if (section.html) {
      collectPbAssetIdsFromString(section.html, assetIds);
    }
    if (section.css) {
      collectPbAssetIdsFromString(section.css, assetIds);
    }
  }

  // We use data URLs here (instead of parent-created blob URLs) because the
  // sandboxed iframe runs in a unique origin and cannot reliably load blob: URLs.
  const urlMap = await resolveAssetIdsToDataUrls(assetIds, {
    onProgress,
    signal,
    offThreadAssets,
    assetSource
  });
  if (urlMap.size === 0) return resolved;

  // Replace pb-asset:// URIs with data URLs in all section HTML/CSS
  for (const section of allSections) {
    if (section.html) section.html = replacePbAssetUris(section.html, urlMap);
    if (section.css) section.css = replacePbAssetUris(section.css, urlMap);
  }

  return resolved;
}

export async function renderToIframe(
  iframe,
  pageState,
  {
    preserveScroll = true,
    hideDuringLoad = false,
    onSettled = null,
    onComplete = null,
    signal = null,
    onPhase = null,
    deferPendingRevoke = false,
    assetSource = null,
    guestFrame = null,
    generation = null,
    __testHooks = null
  } = {}
) {
  const setTimeoutFn =
    typeof __testHooks?.setTimeout === 'function' ? __testHooks.setTimeout : setTimeout;
  const clearTimeoutFn =
    typeof __testHooks?.clearTimeout === 'function' ? __testHooks.clearTimeout : clearTimeout;
  const messageTarget = __testHooks?.messageTarget || window;
  const useGuestFrame = guestFrame !== null;
  if (useGuestFrame && guestFrame !== 'review' && guestFrame !== 'client') {
    throw new TypeError('Unknown guest frame');
  }
  const guestLoadHandlers = new Set();
  const removeFrameLoadListener = (handler) => {
    if (useGuestFrame) guestLoadHandlers.delete(handler);
    else iframe.removeEventListener('load', handler);
  };
  throwIfAborted(signal);
  currentNonce = crypto.randomUUID();
  const nonce = currentNonce;
  const telemetry = createRenderTelemetry(nonce, __testHooks);
  const useSrcdoc = __testHooks?.forceSrcdoc === true || pageState?.flags?.srcdocMode === true;
  const useOffThreadAssets =
    __testHooks?.forceOffThreadAssets === true || pageState?.flags?.offThreadAssets === true;
  let revealMeasured = false;
  let srcSetAt = null;
  let loadAt = null;
  let bridgeReadyAt = null;
  let restoreRequestAt = null;
  const notifyPhase = (phase, progress = null) => {
    if (typeof onPhase !== 'function') return;
    try {
      onPhase({ nonce, phase, progress });
    } catch (err) {
      console.warn('[Renderer] onPhase callback failed:', err);
    }
  };
  notifyPhase('assembling');
  telemetry.start('phase-assemble');
  const siteEffectsReady = loadSiteEffectPartials();
  const globalCSS = await loadGlobalBaseCSS();
  telemetry.end('phase-assemble');

  // If a prior render never reached load/settle, reclaim its pending blob URL now.
  if (pendingRevokeUrl) revokePendingBlobUrl(clearTimeoutFn);

  // Use the bridge's last-reported scroll position (iframe reports via postMessage
  // since parent can't read scrollY from sandboxed cross-origin iframe directly)
  const capturedScroll = preserveScroll ? bridge.lastScroll : { y: 0, zoom: bridge.currentZoom };
  const savedScrollY = preserveScroll ? capturedScroll.y : 0;
  const capturedZoom =
    Number.isFinite(capturedScroll.zoom) && capturedScroll.zoom > 0 ? capturedScroll.zoom : 1;
  const currentZoom =
    Number.isFinite(bridge.currentZoom) && bridge.currentZoom > 0
      ? bridge.currentZoom
      : capturedZoom;
  const shouldDelayReveal = preserveScroll && savedScrollY > 0;
  const shouldHideUntilReady = hideDuringLoad || shouldDelayReveal;

  // Resolve pb-asset:// URIs to blob URLs before assembly
  throwIfAborted(signal);
  telemetry.start('phase-resolve-assets');
  const resolvedState = await resolveAssetUris(pageState, {
    onProgress: (progress) => notifyPhase('resolving-assets', progress),
    signal,
    offThreadAssets: useOffThreadAssets,
    assetSource
  });
  telemetry.end('phase-resolve-assets');
  throwIfAborted(signal);

  telemetry.start('phase-assemble');
  await siteEffectsReady;
  throwIfAborted(signal);
  const templateCSS = await assembleLiveTemplateCSS(resolvedState);
  throwIfAborted(signal);
  // assembleDocumentWarmed, never assembleDocument: the section builders inside
  // it are SYNCHRONOUS and read the vendored map through the sync accessor,
  // whose `null` means "not read yet". assembleLiveTemplateCSS warms a map only
  // for sections needing support CSS, so relying on it would leave every other
  // template hotlinking.
  const html = await assembleDocumentWarmed(
    resolvedState,
    nonce,
    globalCSS,
    assemblePreviewSiteEffectsCSS(resolvedState),
    assemblePreviewSiteEffectsJS(resolvedState),
    getGoTopRingHTML(resolvedState),
    templateCSS,
    { guestFrame: useGuestFrame }
  );
  telemetry.end('phase-assemble');

  const previousBlobUrl = currentBlobUrl;
  throwIfAborted(signal);
  // Guest shells can replace a render while asset assembly is still pending.
  // Reject that old completion before it changes the selected frame document.
  if (useGuestFrame && currentNonce !== nonce) throw createAbortError();
  let nextBlobUrl = null;
  let nextSrcdoc = null;
  telemetry.start('phase-blob-create');
  if (useSrcdoc || useGuestFrame) {
    currentBlobUrl = null;
    nextSrcdoc = html;
  } else {
    const blob = new Blob([html], { type: 'text/html' });
    nextBlobUrl = URL.createObjectURL(blob);
    currentBlobUrl = nextBlobUrl;
  }
  telemetry.end('phase-blob-create');

  const notifySettled = (reason) => {
    const settledAt = nowMs(__testHooks);
    if (Number.isFinite(srcSetAt)) {
      const loadValue = Number.isFinite(loadAt) ? loadAt : settledAt;
      telemetry.set('phase-iframe-load', Math.max(0, loadValue - srcSetAt));
      const bridgeValue = Number.isFinite(bridgeReadyAt) ? bridgeReadyAt : settledAt;
      telemetry.set('phase-bridge-ready', Math.max(0, bridgeValue - srcSetAt));
    }
    if (Number.isFinite(restoreRequestAt)) {
      telemetry.set('phase-scroll-restore', Math.max(0, settledAt - restoreRequestAt));
    } else {
      telemetry.set('phase-scroll-restore', 0);
    }
    if (!revealMeasured) telemetry.set('phase-reveal', 0);
    const completePayload = telemetry.snapshot(reason);
    if (typeof onComplete === 'function') {
      try {
        onComplete(completePayload);
      } catch (err) {
        console.warn('[Renderer] onComplete callback failed:', err);
      }
    }
    if (typeof onSettled !== 'function') return;
    try {
      onSettled({ nonce, reason });
    } catch (err) {
      console.warn('[Renderer] onSettled callback failed:', err);
    }
  };

  // Register reveal/restore handlers BEFORE setting iframe.src so fast loads
  // cannot miss the load event and leave the iframe hidden.
  let abortCleanup = null;
  if (shouldHideUntilReady) {
    const restoreNonce = nonce;
    let settled = false;
    let cleaned = false;
    let restoreFallbackTimer = null;
    let loadFailSafeTimer = null;
    let onLoad = null;
    let onMessage = null;
    let restoreRequested = false;

    const cleanup = () => {
      if (cleaned) return;
      cleaned = true;
      if (onLoad) removeFrameLoadListener(onLoad);
      if (onMessage) messageTarget.removeEventListener('message', onMessage);
      if (restoreFallbackTimer) clearTimeoutFn(restoreFallbackTimer);
      if (loadFailSafeTimer) clearTimeoutFn(loadFailSafeTimer);
    };

    const settle = (reason) => {
      if (settled) return;
      if (currentNonce !== restoreNonce) {
        cleanup();
        return;
      }
      settled = true;
      telemetry.start('phase-reveal');
      iframe.style.visibility = '';
      telemetry.end('phase-reveal');
      revealMeasured = true;
      cleanup();
      if (!deferPendingRevoke) revokePendingBlobUrl(clearTimeoutFn);
      notifySettled(reason);
    };

    const requestScrollRestore = () => {
      if (currentNonce !== restoreNonce) {
        cleanup();
        return;
      }
      if (!shouldDelayReveal || restoreRequested || !iframe.contentWindow) return;
      notifyPhase('restoring-scroll');
      restoreRequestAt = nowMs(__testHooks);
      restoreRequested = true;
      try {
        iframe.contentWindow.postMessage(
          {
            source: 'pb-parent',
            nonce: restoreNonce,
            type: 'restore-scroll',
            data: {
              scrollY: savedScrollY,
              capturedZoom,
              currentZoom
            }
          },
          '*'
        );
      } catch {
        // Sandbox may block — ignore and reveal.
        settle('restore-postmessage-error');
        return;
      }
      restoreFallbackTimer = setTimeoutFn(() => {
        if (currentNonce !== restoreNonce) {
          cleanup();
          return;
        }
        if (!settled) {
          console.warn(
            `[Renderer] Scroll-restore ACK missing after ${RESTORE_FALLBACK_MS}ms; revealing via fallback.`
          );
        }
        settle('restore-fallback');
      }, RESTORE_FALLBACK_MS);
    };

    onMessage = (event) => {
      if (currentNonce !== restoreNonce) return;
      if (event.source !== iframe.contentWindow) return;
      const msg = event.data;
      if (!msg || msg.source !== 'pb-iframe') return;
      if (msg.nonce !== restoreNonce) return;

      if (msg.type === 'bridge-ready') {
        bridgeReadyAt = nowMs(__testHooks);
        if (shouldDelayReveal) requestScrollRestore();
        else settle('bridge-ready');
        return;
      }

      if (shouldDelayReveal && msg.type === 'scroll-restored') {
        settle('scroll-restored');
      }
    };
    messageTarget.addEventListener('message', onMessage);

    onLoad = () => {
      loadAt = nowMs(__testHooks);
      if (!deferPendingRevoke) revokePendingBlobUrl(clearTimeoutFn);
      // Only restore if this is still the current render (not stale)
      if (currentNonce !== restoreNonce || !iframe.contentWindow) {
        cleanup();
        return;
      }

      // Fallback path: bridge-ready should normally arrive first, but if it
      // doesn't, request restore (if needed) once load fires.
      if (shouldDelayReveal) {
        requestScrollRestore();
        return;
      }

      // Hide-during-load path (no scroll restore needed): reveal on load if
      // bridge-ready did not already settle.
      settle('iframe-load');
    };
    if (useGuestFrame) guestLoadHandlers.add(onLoad);
    else iframe.addEventListener('load', onLoad);

    // Fail-safe if load never arrives (navigation interruption, script error, etc.)
    loadFailSafeTimer = setTimeoutFn(() => {
      if (currentNonce !== restoreNonce) {
        cleanup();
        return;
      }
      if (!settled) {
        const phaseSnapshot = telemetry.snapshot('load-failsafe');
        console.warn(
          `[Renderer] Load fail-safe fired after ${LOAD_FAILSAFE_MS}ms; forcing reveal.`,
          {
            renderId: phaseSnapshot.renderId,
            phases: phaseSnapshot.phases
          }
        );
      }
      settle('load-failsafe');
    }, LOAD_FAILSAFE_MS);
    abortCleanup = cleanup;
  } else {
    // Even without hide-during-load, defer old blob revocation until this new doc loads.
    const revokeOnLoad = () => {
      removeFrameLoadListener(revokeOnLoad);
      if (currentNonce !== nonce) return;
      if (!deferPendingRevoke) revokePendingBlobUrl(clearTimeoutFn);
      notifySettled('iframe-load');
    };
    if (useGuestFrame) guestLoadHandlers.add(revokeOnLoad);
    else iframe.addEventListener('load', revokeOnLoad);
    abortCleanup = () => {
      removeFrameLoadListener(revokeOnLoad);
    };
  }

  // Avoid visible "jump to top then back" when restoring prior scroll.
  // We reveal only after restore is applied (or fallback timeout).
  if (shouldHideUntilReady) iframe.style.visibility = 'hidden';
  else iframe.style.visibility = '';

  if (signal?.aborted) {
    if (typeof abortCleanup === 'function') abortCleanup();
    if (currentBlobUrl === nextBlobUrl) currentBlobUrl = previousBlobUrl || null;
    if (!useSrcdoc && !useGuestFrame) {
      try {
        URL.revokeObjectURL(nextBlobUrl);
      } catch {
        // Ignore revocation errors.
      }
    }
    throw createAbortError();
  }

  if (previousBlobUrl && previousBlobUrl !== nextBlobUrl) {
    setPendingBlobRevoke(previousBlobUrl, nonce, { setTimeoutFn, clearTimeoutFn });
  }

  notifyPhase('loading');
  iframe.setAttribute('sandbox', 'allow-scripts');
  srcSetAt = nowMs(__testHooks);
  if (useGuestFrame) {
    const guest = renderGuestFrame(iframe, html, {
      frame: guestFrame,
      generation: generation ?? nonce,
      signal
    });
    const dispose = () => {
      abortCleanup?.();
      guest.dispose();
      signal?.removeEventListener('abort', dispose);
    };
    // Cancellation after ready can still interrupt a pending scroll restore.
    signal?.addEventListener('abort', dispose, { once: true });
    // The empty bootstrap document's load is NOT render completion. Feed the
    // existing reveal/scroll/telemetry callbacks only after the rendered guest
    // document reports ready. Callers attach bridge(iframe, nonce), then await
    // this promise; attaching only after ready would miss initial bridge events.
    const ready = guest.ready.then(
      (result) => {
        for (const handler of [...guestLoadHandlers]) handler();
        return result;
      },
      (error) => {
        dispose();
        throw error;
      }
    );
    return {
      nonce,
      blobUrl: null,
      mode: 'guest',
      generation: guest.generation,
      ready,
      setCommentMode: guest.setCommentMode,
      dispose
    };
  }
  if (useSrcdoc) {
    iframe.removeAttribute('src');
    iframe.srcdoc = nextSrcdoc;
  } else {
    iframe.removeAttribute('srcdoc');
    iframe.src = nextBlobUrl;
  }

  return { nonce, blobUrl: nextBlobUrl, mode: useSrcdoc ? 'srcdoc' : 'blob' };
}

export function getCurrentNonce() {
  return currentNonce;
}

export function flushPendingBlobRevoke() {
  revokePendingBlobUrl(clearTimeout);
}
